import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import callApi, { abortableCallApi, callDownload } from 'utils/callApi';
import { LOCATION_CHANGE } from 'react-router-redux';

import { fetchAccountInformationSuccess } from 'redux/modules/account';
import { fetchAppInfoSuccess } from 'redux/modules/appInfo';
import { addNotification } from 'redux/modules/notifications';

// Actions
export const FETCH_USERS = 'FETCH_USERS';
export const FETCH_USERS_SUCCESS = 'FETCH_USERS_SUCCESS';
export const FETCH_USERS_FAIL = 'FETCH_USERS_FAIL';
export const CLEAR_USERS = 'CLEAR_USERS';
export const FETCH_USER = 'FETCH_USER';
export const FETCH_USER_SUCCESS = 'FETCH_USER_SUCCESS';
export const FETCH_USER_FAIL = 'FETCH_USER_FAIL';
export const CREATE_USER = 'CREATE_USER';
export const CREATE_USER_SUCCESS = 'CREATE_USER_SUCCESS';
export const CREATE_USER_FAIL = 'CREATE_USER_FAIL';
export const UPDATE_USER = 'UPDATE_USER';
export const UPDATE_USER_SUCCESS = 'UPDATE_USER_SUCCESS';
export const UPDATE_USER_FAIL = 'UPDATE_USER_FAIL';
export const DELETE_USER = 'DELETE_USER';
export const DELETE_USER_SUCCESS = 'DELETE_USER_SUCCESS';
export const DELETE_USER_FAIL = 'DELETE_USER_FAIL';
export const CHANGE_USER_PASSWORD = 'CHANGE_USER_PASSWORD';
export const CHANGE_USER_PASSWORD_SUCCESS = 'CHANGE_USER_PASSWORD_SUCCESS';
export const CHANGE_USER_PASSWORD_FAIL = 'CHANGE_USER_PASSWORD_FAIL';
export const ADD_USER_RESOURCE = 'ADD_USER_RESOURCE';
export const ADD_USER_RESOURCE_SUCCESS = 'ADD_USER_RESOURCE_SUCCESS';
export const ADD_USER_RESOURCE_FAIL = 'ADD_USER_RESOURCE_FAIL';
export const REMOVE_USER_RESOURCE = 'REMOVE_USER_RESOURCE';
export const REMOVE_USER_RESOURCE_SUCCESS = 'REMOVE_USER_RESOURCE_SUCCESS';
export const REMOVE_USER_RESOURCE_FAIL = 'REMOVE_USER_RESOURCE_FAIL';
export const GENERATE_USER_ACTIVITY_LOG = 'GENERATE_USER_ACTIVITY_LOG';
export const GENERATE_USER_ACTIVITY_LOG_SUCCESS = 'GENERATE_USER_ACTIVITY_LOG_SUCCESS';
export const GENERATE_USER_ACTIVITY_LOG_FAIL = 'GENERATE_USER_ACTIVITY_LOG_FAIL';

// Reducer
export default function usersReducer(state = {
  data: [], count: 0, loading: 0,
}, action) {
  const { type } = action;
  let triggerLoading;
  let payload;
  if (action.payload) {
    ({ triggerLoading, ...payload } = action.payload);
  }
  switch (type) {
    case FETCH_USERS:
      return {
        ...state,
        ...triggerLoading && state.loading <= 0 ? { loading: state.loading + 1 } : {},
      };
    case FETCH_USERS_SUCCESS: {
      if (payload.push) {
        const newData = [
          ...state.data.slice(0, state.data.length),
          ...payload.data.slice(0, payload.data.length),
        ];
        return {
          ...state,
          count: payload.count,
          data: newData,
          ...triggerLoading ? { loading: state.loading > 0 ? state.loading - 1 : 0 } : {},
        };
      }
      return {
        ...state,
        ...payload,
        ...triggerLoading ? { loading: state.loading > 0 ? state.loading - 1 : 0 } : {},
      };
    }
    case FETCH_USERS_FAIL:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case CLEAR_USERS:
      return {
        ...state,
        data: [],
        count: 0,
      };
    case FETCH_USER:
      return {
        ...state,
        ...triggerLoading && state.loading <= 0 ? { loading: state.loading + 1 } : {},
      };
    case FETCH_USER_SUCCESS: {
      const newData = [
        ...state.data.slice(0, state.data.length),
        payload,
      ];
      return {
        ...state,
        count: state.count + 1,
        data: newData,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    }
    case FETCH_USER_FAIL:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case UPDATE_USER_SUCCESS:
    case ADD_USER_RESOURCE_SUCCESS:
    case REMOVE_USER_RESOURCE_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        if (dataItem.ID !== payload.ID) {
          return dataItem;
        }
        return {
          ...dataItem,
          ...payload,
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    case DELETE_USER_SUCCESS: {
      const index = state.data.findIndex(dataItem => dataItem.ID === payload.ID);
      const newData = [
        ...state.data.slice(0, index),
        ...state.data.slice(index + 1),
      ];
      return {
        ...state,
        count: state.count - 1,
        data: newData,
      };
    }
    case LOCATION_CHANGE:
      return {
        ...state,
        loading: 0,
      };
    default:
      return state;
  }
}

// Action Creators
export function fetchUsers(payload) {
  return { type: FETCH_USERS, payload };
}

export function fetchUsersSuccess(payload) {
  return { type: FETCH_USERS_SUCCESS, payload };
}

export function fetchUsersFail(payload) {
  return { type: FETCH_USERS_FAIL, payload };
}

export function clearUsers() {
  return { type: CLEAR_USERS };
}

export function fetchUser(payload) {
  return { type: FETCH_USER, payload };
}

export function fetchUserSuccess(payload) {
  return { type: FETCH_USER_SUCCESS, payload };
}

export function fetchUserFail(payload) {
  return { type: FETCH_USER_FAIL, payload };
}

export function createUser(payload) {
  return { type: CREATE_USER, payload };
}

export function createUserSuccess(payload) {
  return { type: CREATE_USER_SUCCESS, payload };
}

export function createUserFail(payload) {
  return { type: CREATE_USER_FAIL, payload };
}

export function updateUser(payload) {
  return { type: UPDATE_USER, payload };
}

export function updateUserSuccess(payload) {
  return { type: UPDATE_USER_SUCCESS, payload };
}

export function updateUserFail(payload) {
  return { type: UPDATE_USER_FAIL, payload };
}

export function deleteUser(payload) {
  return { type: DELETE_USER, payload };
}

export function deleteUserSuccess(payload) {
  return { type: DELETE_USER_SUCCESS, payload };
}

export function deleteUserFail(payload) {
  return { type: DELETE_USER_FAIL, payload };
}

export function changeUserPassword(payload) {
  return { type: CHANGE_USER_PASSWORD, payload };
}

export function changeUserPasswordSuccess(payload) {
  return { type: CHANGE_USER_PASSWORD_SUCCESS, payload };
}

export function changeUserPasswordFail(payload) {
  return { type: CHANGE_USER_PASSWORD_FAIL, payload };
}

export function addUserResource(payload) {
  return { type: ADD_USER_RESOURCE, payload };
}

export function addUserResourceSuccess(payload) {
  return { type: ADD_USER_RESOURCE_SUCCESS, payload };
}

export function addUserResourceFail(payload) {
  return { type: ADD_USER_RESOURCE_FAIL, payload };
}

export function removeUserResource(payload) {
  return { type: REMOVE_USER_RESOURCE, payload };
}

export function removeUserResourceSuccess(payload) {
  return { type: REMOVE_USER_RESOURCE_SUCCESS, payload };
}

export function removeUserResourceFail(payload) {
  return { type: REMOVE_USER_RESOURCE_FAIL, payload };
}

export function generateUserActivityLog(payload) {
  return { type: GENERATE_USER_ACTIVITY_LOG, payload };
}

export function generateUserActivityLogSuccess(payload) {
  return { type: GENERATE_USER_ACTIVITY_LOG_SUCCESS, payload };
}

export function generateUserActivityLogFail(payload) {
  return { type: GENERATE_USER_ACTIVITY_LOG_FAIL, payload };
}

// Requests

let abort;

export function requestFetchUsers({ limit, offset }) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = abortableCallApi(`settings/user/?limit=${limit}&offset=${offset}`);
  abort = request.abort;
  return request.res;
}

export function requestSearchUsers({ limit, offset, filters }) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = abortableCallApi('settings/user/search/', 'POST', {
    filters,
    sort_by: [
      {
        name: 'First Name',
        sort: 'asc',
      },
      {
        name: 'ID',
        sort: 'asc',
      },
    ],
    limit,
    offset,
  });
  abort = request.abort;
  return request.res;
}

export function requestFetchUser(payload) {
  return callApi(`settings/user/?id=${payload}`);
}

export function requestCreateUser(payload) {
  return callApi('settings/user/', 'POST', payload);
}

export function requestUpdateUser(payload) {
  return callApi('settings/user/', 'PATCH', payload);
}

export function requestDeleteUser(payload) {
  return callApi(`settings/user/?id=${payload}`, 'DELETE');
}

export function requestCreateUserDocument(payload) {
  return callApi('settings/document/', 'POST', payload, false);
}

export function requestAddUserResource(payload) {
  return callApi('settings/user/change_resource/add/', 'POST', payload);
}

export function requestRemoveUserResource(payload) {
  return callApi('settings/user/change_resource/remove/', 'POST', payload);
}

export function requestChangeUserPassword(payload) {
  return callApi('settings/user/change_password/', 'POST', payload);
}

export function requestGenerateUserActivityLog({ fileName, ...otherProps }) {
  return callDownload('report/order_report/create_on_generate/', 'POST', fileName, otherProps);
}

// Sagas
function* fetchUsersSaga(action) {
  const { triggerLoading, filters } = action.payload;
  try {
    const res = filters
      ? yield call(requestSearchUsers, action.payload)
      : yield call(requestFetchUsers, action.payload);
    yield put(fetchUsersSuccess({
      data: res.data,
      count: res.count,
      triggerLoading,
      ...action.payload.push ? { push: true } : {},
    }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    yield put(fetchUsersFail({
      ...err.data,
      triggerLoading,
    }));
  }
}

function* fetchUserSaga(action) {
  const { triggerLoading } = action.payload;
  try {
    const res = yield call(requestFetchUser, action.payload.ID);
    if (res.data.ID) {
      yield put(fetchUserSuccess({
        ...res.data,
        triggerLoading,
      }));
    } else {
      yield put(fetchUserFail({
        triggerLoading,
      }));
    }
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    yield put(fetchUserFail({
      ...err.data,
      triggerLoading,
    }));
  }
}

function* createUserSaga(action) {
  const {
    values: {
      Image,
      ...otherValues
    },
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestCreateUser, otherValues);
    yield put(createUserSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    if (Image) {
      const formData = new FormData();
      formData.append('object_id', res.data.ID);
      formData.append('file', Image);
      const image = yield call(requestCreateUserDocument, formData);
      yield call(requestUpdateUser, { ID: res.data.ID, 'Image ID': image.data.ID });
      yield put(addNotification({
        message: 'New user was successfully created',
        variant: 'success',
      }));
      if (resolve) {
        resolve(res.data);
      }
    } else {
      yield put(addNotification({
        message: 'New user was successfully created',
        variant: 'success',
      }));
      if (resolve) {
        resolve(res.data);
      }
    }
  } catch (err) {
    yield put(createUserFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* updateUserSaga(action) {
  const {
    values: {
      ID,
      Image,
      ...otherValues
    },
    resolve,
    reject,
  } = action.payload;
  try {
    let res;
    if (Object.keys(otherValues).length > 0) {
      res = yield call(requestUpdateUser, { ID, ...otherValues });
      yield put(updateUserSuccess(res.data));
      yield put(fetchAccountInformationSuccess(res.user_info));
      yield put(fetchAppInfoSuccess(res.app_info));
    }
    if (Image) {
      const formData = new FormData();
      formData.append('object_id', ID);
      if (Image.noFileConstructor) {
        delete Image.noFileConstructor;
        formData.append('file', Image, Image.name);
      } else {
        formData.append('file', Image);
      }
      const image = yield call(requestCreateUserDocument, formData);
      const user = yield call(requestUpdateUser, { ID, 'Image ID': image.data.ID });
      yield put(updateUserSuccess(user.data));
      yield put(fetchAccountInformationSuccess(user.user_info));
      yield put(fetchAppInfoSuccess(user.app_info));
      yield put(addNotification({
        message: 'User was successfully updated',
        variant: 'success',
      }));
      if (resolve) {
        resolve(user.data);
      }
    } else {
      if (otherValues['Saved Filters'] === null) {
        yield put(addNotification({
          message: 'Your Filters were successfully cleared',
          variant: 'success',
        }));
      }  else {
        yield put(addNotification({
          message: otherValues['Saved Filters']
            ? 'Your Filters were successfully saved'
            : 'User was successfully updated',
          variant: 'success',
        }));
      }
      if (resolve) {
        resolve(res.data);
      }
    }
  } catch (err) {
    yield put(updateUserFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* deleteUserSaga(action) {
  const { ID } = action.payload;
  try {
    const res = yield call(requestDeleteUser, ID);
    yield put(deleteUserSuccess({ ID }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'User was successfully deleted',
      variant: 'success',
    }));
  } catch (err) {
    yield put(deleteUserFail(err.data));
    yield put(addNotification({
      message: 'Failed to delete user',
      variant: 'warning',
    }));
  }
}

function* changeUserPasswordSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    yield call(requestChangeUserPassword, values);
    yield put(changeUserPasswordSuccess());
    if (resolve) {
      resolve();
    }
  } catch (err) {
    yield put(changeUserPasswordFail(err));
    if (reject) {
      reject(err);
    }
  }
}

function* addUserResourceSaga(action) {
  const {
    resolve,
    reject,
    values,
  } = action.payload;
  try {
    const res = yield call(requestAddUserResource, values);
    yield put(addUserResourceSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(addUserResourceFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* removeUserResourceSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestRemoveUserResource, values);
    yield put(removeUserResourceSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(removeUserResourceFail(err.data));
    if (reject) {
      reject(err);
    }
  }
}

function* generateUserActivityLogSaga(action) {
  try {
    const res = yield call(requestGenerateUserActivityLog, action.payload);
    if (res.error) {
      yield put(generateUserActivityLogFail(res.error));
      yield put(addNotification({
        message: res.error,
        variant: 'warning',
      }));
    } else {
      yield put(generateUserActivityLogSuccess(res.data));
    }
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    yield put(generateUserActivityLogFail(err.data));
    yield put(addNotification({
      message: err.data,
      variant: 'warning',
    }));
  }
}

export function* usersSaga() {
  yield takeLatest(FETCH_USERS, fetchUsersSaga);
  yield takeLatest(FETCH_USER, fetchUserSaga);
  yield takeEvery(CREATE_USER, createUserSaga);
  yield takeEvery(UPDATE_USER, updateUserSaga);
  yield takeEvery(DELETE_USER, deleteUserSaga);
  yield takeEvery(CHANGE_USER_PASSWORD, changeUserPasswordSaga);
  yield takeEvery(ADD_USER_RESOURCE, addUserResourceSaga);
  yield takeEvery(REMOVE_USER_RESOURCE, removeUserResourceSaga);
  yield takeEvery(GENERATE_USER_ACTIVITY_LOG, generateUserActivityLogSaga);
}



// WEBPACK FOOTER //
// ./src/redux/modules/users.js